const { orderedItems, sectionTotals } = require("./quote-line-structure");

// Commercial document boundary, mirrored byte-for-byte in the browser.
// Amounts are supplied by the financial engine/read contract, never recalculated.
const uncertain = () => Object.assign(new Error("Visibilité du document client non vérifiable. Vérifiez les sections et les lignes."),
  { status: 409, code: "QUOTE_CLIENT_VISIBILITY_UNVERIFIABLE" });
const text = (value) => typeof value === "string" ? value.trim() : "";
const hidden = (value) => value?.isClientVisible === false || value?.is_client_visible === false;
const technical = (value) => ["importe depuis planning ao/dce", "charge issue du planning ao/dce"].includes(
  text(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    .replace(/\s*\/\s*/g, "/").replace(/\s+/g, " ").replace(/[.!?]+$/g, ""));
const scalar = (value) => typeof value === "string" || typeof value === "number" ? value : null;
const commercialFields = (source = {}) => ({
  objectText: text(source.objectText), scopeText: text(source.scopeText),
  exclusionsText: text(source.exclusionsText), notesClient: text(source.notesClient),
  paymentTerms: text(source.paymentTerms), executionDelay: text(source.executionDelay),
  validUntil: text(source.validUntil),
  validityDays: scalar(source.validityDays),
});

const projectClientSections = (sources = [], results = [], calculated = {}) => {
  const totals = new Map(sectionTotals(calculated).map((item) => [String(item.id), item.totalHT]));
  const sections = new Map(), sourceLines = new Set(), resultLines = new Map();
  for (const result of results || []) {
    if (result.id == null || resultLines.has(String(result.id))) throw uncertain();
    resultLines.set(String(result.id), result);
  }
  for (const source of sources || []) {
    if (source.id == null || sections.has(String(source.id))) throw uncertain();
    sections.set(String(source.id), source);
    for (const line of source.lines || []) {
      if (line.id == null || sourceLines.has(String(line.id))) throw uncertain();
      sourceLines.add(String(line.id));
    }
  }
  for (const id of resultLines.keys()) if (!sourceLines.has(id)) throw uncertain();
  const visible = (id, path = new Set()) => {
    const section = sections.get(id);
    if (!section || path.has(id)) throw uncertain();
    const parent = section.parentSectionId ?? section.parent_section_id;
    // Validate the complete ancestry, including below a hidden parent.
    const ancestorVisible = parent == null || visible(String(parent), new Set([...path, id]));
    return ancestorVisible && !hidden(section) && !technical(section.title);
  };
  const projected = new Map();
  for (const [id, source] of sections) {
    if (!visible(id)) continue;
    const lines = orderedItems(source.lines || []).flatMap((line) => {
      if (hidden(line) || technical(line.description)) return [];
      if (line.lineType === "text") return [{ id: scalar(line.id), lineType: "text", description: text(line.description) }];
      // Historical subtotal rows have no financial aggregation contract.
      if (line.lineType === "subtotal") return [{ id: scalar(line.id), lineType: "subtotal", description: text(line.description) }];
      const result = resultLines.get(String(line.id));
      if (!result || result.totalHT == null || result.totalHT === "" || !Number.isFinite(Number(result.totalHT))) throw uncertain();
      for (const value of [result.totalVat, result.totalTTC, result.unitPriceHT]) {
        if (value != null && (value === "" || !Number.isFinite(Number(value)))) throw uncertain();
      }
      return [{ id: scalar(line.id), lineType: "service", code: text(line.code),
        description: text(line.description), lineComment: text(line.lineComment),
        quantity: scalar(result.quantity ?? line.quantity), unit: text(line.unit),
        unitPriceHT: scalar(result.unitPriceHT), totalHT: scalar(result.totalHT),
        totalVat: scalar(result.totalVat), totalTTC: scalar(result.totalTTC) }];
    });
    projected.set(id, { id: scalar(source.id), code: text(source.code), title: text(source.title),
      description: technical(source.description) ? "" : text(source.description),
      orderIndex: source.orderIndex, totalHT: scalar(totals.get(id)), lines, children: [] });
  }
  const roots = [];
  for (const [id, node] of projected) {
    const source = sections.get(id), parent = source.parentSectionId ?? source.parent_section_id;
    if (parent == null) roots.push(node);
    else projected.get(String(parent)).children.push(node);
  }
  const sort = (items) => orderedItems(items).map((item) => ({ ...item, children: sort(item.children) }));
  return sort(roots);
};

module.exports = { commercialFields, projectClientSections };
